import { Nullable } from 'types';
import { Cancelable, Response } from '../../network/client/types';
import {
  AddressResponse,
  BillingDetailsResponse,
  CountryResponse,
  CustomerResponse,
  CustomerSessionResponse,
  DateResponse,
  ErrorResponse,
  IntentResponse,
  InvoiceResponse,
  PaginationDataResponse,
  PriceResponse,
  PrivateUserResponse,
  ProductInformationResponse,
  ProductResponse,
  PublicUserResponse,
  RegisterResponse,
  SongDownloadResponse,
  SongPeaksResponse,
  SongListResponse,
  SubscriptionInfoResponse,
  SubscriptionResponse,
  TagResponse,
  TagsListResponse,
  TokenResponse,
  LibrarySongListResponse,
  LibrarySongResponse,
  BaseSongResponse,
  MainSongResponse,
} from '../../responses/responseTypes';

const responseData = () => {
  const token: TokenResponse = {
    access_token: 'Access Token',
    refresh_token: 'Refresh Token',
    token_type: 'Token Type',
    scope: null,
    expires_in: 3600,
  };

  const httpError: ErrorResponse = {
    status: 400,
    data: {
      error: 'Error Title',
      error_description: 'Error Description',
    },
  };

  const tag: TagResponse = {
    name: 'tagName',
    id: 2,
    active: true,
  };

  const paginationData: PaginationDataResponse = {
    current_page: 1,
    last_page: 1,
    items_per_page: 10,
    items_count: 0,
    total_pages: 0,
    total_items: 0,
  };

  const tagsList: TagsListResponse = {
    items: [tag],
    pagination_data: paginationData,
  };
  const createTagResponse: TagResponse = tag;

  const register: RegisterResponse = {
    id: 'userId',
    first_name: 'firstName',
    last_name: 'lastName',
    email: 'email',
    profile_image_path: 'profileImagePath',
    subscription_type: 'subscriptionType',
  };

  const creationDate: DateResponse = {
    date: '2020-03-26 14:23:40.000000',
    timezone_type: 3,
    timezone: 'Europe/Berlin',
  };

  const subscriptionInfo: SubscriptionInfoResponse = {
    id: 'subId',
    payment_details: {
      last4: 1234,
    },
    period: {
      start: {
        date: '2020-03-26 14:23:40.000000',
        timezone_type: 1,
        timezone: 'test timezone',
      },
      end: {
        date: '2020-03-30 14:23:40.000000',
        timezone_type: 1,
        timezone: 'test timezone',
      },
    },
    price: {
      currency: 'usd',
      unit_amount: 123,
    },
    product: {
      id: 'productId',
      name: 'productName',
    },
    recurring: {
      interval: 'month',
      interval_count: 1,
    },
    status: 'active',
  };

  const emailConfirmed = {
    email_confirmed: true,
  };

  const emailConfirmationLinkSent = {
    email_sent: true,
  };

  const publicUser: PublicUserResponse = {
    id: 'userId',
    email: 'userEmail',
    first_name: 'userFirstName',
    last_name: 'userLastName',
    profile_image_path: 'userProfileImagePath',
  };

  const privateUser: PrivateUserResponse = {
    ...publicUser,
    roles: ['role1', 'role2'],
    email_confirmed: true,
    muted: false,
    subscription: subscriptionInfo,
    // @ts-ignore
    subscription_type: 'subscriptionType',
    created_at: creationDate,
    download_limit: {
      is_warning: true,
      downloads_available_today: 5,
      days_to_limit_reset: 1,
    },
    download_limit_stems: {
      is_warning: true,
      downloads_available_today: 5,
      days_to_limit_reset: 1,
    },
    download_limit_sample_packs: {
      is_warning: true,
      downloads_available_today: 5,
      days_to_limit_reset: 1,
    },
    add_ons_info: {
      distribution_included: true,
    },
  };

  const peaksData: SongPeaksResponse = {
    version: 2,
    channels: 2,
    sample_rate: 48000,
    samples_per_pixel: 512,
    bits: 8,
    length: 3,
    data: [-65, 63, -66, 64, -40, 41, -39, 45, -55, 43, -55, 44],
  };

  const baseSong: BaseSongResponse = {
    id: 'songId',
    title: 'songTitle',
    duration: 10000,
    music_file_path: 'musicFilePath',
    wav_file_path: 'songFilePath',
    is_in_library: false,
    formula: null,
  };

  const freeSong: MainSongResponse = {
    ...baseSong,
    is_free: true,
    has_XML: false,
    tempo_bpm: 120,
    has_vocal: true,
    genres: [tag],
    moods: [tag],
    secondary_genres: [tag],
    instruments: [tag],
    keys: [tag],
    energies: [tag],
    eras: [tag],
  };

  const customizableSong: MainSongResponse = {
    ...baseSong,
    is_free: false,
    tempo_bpm: 120,
    has_vocal: true,
    has_XML: true,
    genres: [tag],
    moods: [tag],
    secondary_genres: [tag],
    instruments: [tag],
    keys: [tag],
    energies: [tag],
    eras: [tag],
  };

  const librarySong: LibrarySongResponse = {
    id: 'entityId',
    song: customizableSong,
    vega_song: null,
  };

  const songInLibrary: BaseSongResponse = { ...freeSong, is_in_library: true };

  const songsList: SongListResponse = {
    items: [freeSong, customizableSong],
    pagination_data: paginationData,
  };

  const librarySongList: LibrarySongListResponse = {
    items: [librarySong],
    pagination_data: paginationData,
  };

  const price: PriceResponse = {
    id: 'priceId',
    currency: 'usd',
    recurring: {
      interval: 'month',
      interval_count: 12,
    },
    unit_amount: 12345,
  };

  const product: ProductResponse = {
    id: 'productId',
    name: 'productName',
    prices: [price],
    metadata: {
      product_information: 'productInformationURL',
    },
    is_virtual: false,
    is_add_on: false,
    add_ons_info: {
      distribution_included: false,
    },
  };

  const productInformation: ProductInformationResponse = {
    title: 'productTitle',
    subtitle: 'productSubtitle',
    description: 'productDescription',
    focused: true,
    free: true,
    price: 'from_stripe',
    productType: 'productType',
    index: 0,
    features: [
      {
        name: 'featureName',
        active: false,
        comingSoon: false,
      },
    ],
    licenseInfo: {
      title: 'licenseTitle',
      description: 'licenseDescription',
    },
  };

  const intent: IntentResponse = {
    id: 'intentId',
    status: 'succeeded',
    client_secret: 'clientSecret',
  };

  const country: CountryResponse = {
    id: 'us',
    object: 'country',
    default_currency: 'usd',
    supported_bank_account_currencies: {},
    supported_payment_currencies: ['usd'],
    supported_payment_methods: ['card'],
    supported_transfer_countries: ['us'],
    verification_fields: {
      company: {
        additional: [],
        minimum: [],
      },
      individual: {
        additional: [],
        minimum: [],
      },
    },
  };

  const address = (line1: Nullable<string>): AddressResponse => ({
    line1,
    line2: 'line2',
    city: 'city',
    country: 'country',
    postal_code: 'postal_code',
    state: 'state',
  });

  const billingDetails = (line1: Nullable<string>): BillingDetailsResponse => ({
    first_name: 'first_name',
    last_name: 'last_name',
    price_id: 'price_id',
    payment_method_id: 'payment_method_id',
    coupon: 'coupon',
    address: address(line1),
  });

  const customer: CustomerResponse = {
    id: 'customerId',
    email: 'customerEmail',
    address: address('line1'),
    invoice_settings: {
      default_payment_method: 'defaultPaymentMethodId',
    },
  };

  const customerSession: CustomerSessionResponse = {
    id: 'testId',
    object: 'billing_portal',
    created: 0,
    customer: 'testCustomer',
    livemode: false,
    return_url: 'http://test.test',
    url: 'https://redirect.to',
  };

  const invoice: InvoiceResponse = {
    id: 'invoiceId',
    customer: 'customerId',
    payment_intent: intent,
  };

  const subscription: SubscriptionResponse = {
    id: 'subscriptionId',
    status: 'active',
    current_period_end: 12345,
    customer,
    latest_invoice: invoice,
    pending_setup_intent: intent,
    items: {
      data: [
        {
          price: {
            id: 'priceId',
          },
        },
      ],
    },
  };

  const songDownloadInfo: SongDownloadResponse = {
    download_data: {
      id: 'songId',
      name: 'songName',
      url: 'songURL',
      size: 999,
      download_limit: {
        is_warning: true,
        downloads_available_today: 5,
        days_to_limit_reset: 2,
      },
    },
  };

  const mockResponse = <T>(
    data: T,
    isCanceled = false
  ): Response<T> & Cancelable => ({
    data,
    status: 200,
    isCanceled,
  });

  return Object.freeze({
    token,
    httpError,
    songInLibrary,
    songsList,
    librarySongList,
    paginationData,
    register,
    emailConfirmed,
    emailConfirmationLinkSent,
    publicUser,
    privateUser,
    tag,
    tagsList,
    createTagResponse,
    peaksData,
    address,
    billingDetails,
    country,
    price,
    product,
    productInformation,
    customer,
    customerSession,
    invoice,
    subscription,
    songDownloadInfo,
    baseSong,
    freeSong,
    customizableSong,
    mockResponse,
  });
};

export default responseData;
